import React, { useState } from "react";
import { useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Stack,
} from "@mui/material";
import {
  Edit as EditIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import EditModal from './EditModal';
import { getLob } from "../../services/authenticationService";
import { updateConfigRequestBody } from "../../services/managePoliciesService";
import { validateWithOtp } from '../../stateManagement/actions/validateOtpPopupActions';
import { metaDataBatchPayload } from "../../types/types";
import { META_DATA_BATCH_API } from "../../services/networkService";
import { openPopup } from "../../stateManagement/actions/popupActions";

interface TaskStatusProps {
  activeModules: any[];
  configData: any[];
  taskCounts: Record<string, number>;
  onModuleClick: (moduleName: string) => void;
  onModulesUpdate?: (updatedModules: any[]) => void;
}

const TaskStatus: React.FC<TaskStatusProps> = ({
  activeModules,
  configData,
  taskCounts,
  onModuleClick,
  onModulesUpdate
}) => {
  const { selectedModule } = useSelector((state: any) => state.loyaltyManagement);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [moduleToEdit, setModuleToEdit] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(activeModules);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    if (onModulesUpdate) {
      onModulesUpdate(items);
    }
  };

  const handleModuleClick = (module: any) => {
    onModuleClick(module.taskName);
  };

  const handleEditClick = (module: any, event: React.MouseEvent) => {
    event.stopPropagation();
    setModuleToEdit(module);
    setEditModalOpen(true);
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setModuleToEdit(null);
  };

  const handleModuleSave = (updatedModule: any) => {
    const updatedModules = activeModules.map(module =>
      module.taskName === updatedModule.taskName ? updatedModule : module
    );
    if (onModulesUpdate) {
      onModulesUpdate(updatedModules);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const lob = getLob();
      const existingDomainValues = configData || [];
      const loyaltyTaskIndex = existingDomainValues.findIndex(
        (item: any) => item.name === "LoyaltyTaskInfo"
      );

      let updatedDomainValues;

      if (loyaltyTaskIndex !== -1) {
        updatedDomainValues = [
          ...existingDomainValues.slice(0, loyaltyTaskIndex),
          {
            ...existingDomainValues[loyaltyTaskIndex],
            value: activeModules,
          },
          ...existingDomainValues.slice(loyaltyTaskIndex + 1),
        ];
      } else {
        updatedDomainValues = [
          ...existingDomainValues,
          {
            name: "LoyaltyTaskInfo",
            type: "List",
            value: activeModules,
            docUrl: "",
            description: "",
            defaultValue: [],
            possibleVals: [],
          },
        ];
      }

      const requestBody: updateConfigRequestBody = {
        domainName: "clientconfig",
        domainType: "LoyaltyConfig",
        domainValues: updatedDomainValues,
        lob,
      };

      const finalMetaDataPayload: metaDataBatchPayload = {
        features: [requestBody],
      };

      validateWithOtp(
        (verifiedUser?: any, verifyResponse?: any) => {
          try {
            if (verifyResponse && (verifyResponse.status === 200 || verifyResponse.status === 201)) {
              openPopup("Success", "Modules updated successfully!");
            } else {
              openPopup("Error", "Something went wrong while saving modules.");
              throw new Error("API Error");
            }
          } catch (error) {
            openPopup("Error", "Something went wrong while saving modules.");
          } finally {
            setLoading(false);
          }
        },
        ["demo", "prod", "prod-egtm"],
        "admin",
        META_DATA_BATCH_API,
        finalMetaDataPayload,
        "PUT"
      );
    } catch (error) {
      setLoading(false);
      openPopup("Error", "Failed to update module metadata.");
      console.error("Error in handleSave:", error);
    }
  };

  return (
    <>
      <Box sx={{
        borderRadius: 2,
        ml: 0,
        height: '100%',
        minHeight: 420,
        border: '1px solid #DAE3E6',
        bgcolor: 'white',
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        flex: 1
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          flexShrink: 0
        }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: '#02C7AF',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: '16px'
            }}
          >
            • Active Task Category ({activeModules.length})
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{
              px: 2,
              py: 0.5,
              fontSize: '13px',
              minWidth: 'auto',
              height: '32px',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none'
              }
            }}
          >
            Save
          </Button>
        </Box>

        <Box sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="modules">
              {(provided) => (
                <Stack
                  spacing={1.5}
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{ flex: 1, minHeight: 0 }}
                >
                  {activeModules.map((module, index) => (
                    <Draggable key={module.taskName} draggableId={module.label} index={index}>
                      {(provided, snapshot) => (
                        <Card
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          onClick={() => handleModuleClick(module)}
                          sx={{
                            borderRadius: 2,
                            border: selectedModule === module.taskName
                              ? "1.5px solid #02C7AF"
                              : "1px solid #DAE3E6",
                            bgcolor:
                              selectedModule === module.taskName
                                ? "#EFFFFC"
                                : snapshot.isDragging
                                  ? "#f8f9fa"
                                  : "white",
                            boxShadow: "none",
                            "&:hover": {
                              bgcolor: "#f8f9fa",
                              cursor: "pointer",
                            },
                            "& .MuiCardContent-root": {
                              padding: "8px 8px",
                              "&:last-child": {
                                paddingBottom: "6px",
                              },
                            },
                            flexShrink: 0
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ display: 'flex', gap: 1.5, flex: 1, alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Box {...provided.dragHandleProps} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', height: '40px' }}>
                                    <MenuIcon fontSize="small" sx={{ color: '#999' }} />
                                  </Box>

                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Box
                                      sx={{
                                        width: 25,
                                        height: 25,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <img
                                        src={module.icon}
                                        alt={module.label}
                                        style={{ width: 25, height: 25, objectFit: "contain" }}
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '20px' }}>
                                    <span style={{ fontWeight: 600, fontSize: '15px' }}>
                                      {module.label}
                                    </span>
                                  </Box>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                                    {module.subHeading}
                                  </Typography>
                                </Box>
                              </Box>

                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  sx={{
                                    color: '#666',
                                    '&:hover': {
                                      bgcolor: 'rgba(2, 199, 175, 0.1)'
                                    }
                                  }}
                                  onClick={(e) => handleEditClick(module, e)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: '#02C7AF', fontWeight: 500 }}>
                                Task: {taskCounts[module.taskName] || 0}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#02C7AF",
                                  fontWeight: 500,
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    bgcolor: "#3BCDB7",
                                    color: "#fff",
                                  },
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleModuleClick(module);
                                }}
                              >
                                See Tasks {'>'}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      </Box>
      <EditModal
        open={editModalOpen}
        module={moduleToEdit}
        onClose={handleModalClose}
        onSave={handleModuleSave}
      />
    </>
  );
};

export default TaskStatus;